import { FormattedMessage } from 'react-intl';
import Typography from '@alltrails/core/components/Typography';
import Button from '@alltrails/denali/components/Button';
import { useCallback, useEffect } from 'react';
import { ModalStep } from '@alltrails/modal';
import { type User } from '@alltrails/types/User';
import useUser from '@alltrails/context/hooks/useUser';
import type PurchaseTier from '@alltrails/core/types/PurchaseTier';
import PeakLogoBadge from '@alltrails/denali/components/SubscriptionBadge/Logo/Peak';
import PlusLogoBadge from '@alltrails/denali/components/SubscriptionBadge/Logo/Plus';
import { useExperiments } from '@alltrails/experiments';
import styles from './CheckoutWelcome.module.scss';

type Props = {
  closeModal: (user: User) => void;
  purchaseTier: PurchaseTier;
};

// https://www.figma.com/design/dOj6Te4I0TVguYevn6Fj5g/Web-Checkout-Improvements?node-id=334-31701&m=dev
// https://www.figma.com/design/dOj6Te4I0TVguYevn6Fj5g/Web-Checkout-Improvements?node-id=375-33022&m=dev
// https://www.figma.com/design/CAAZrtC2gy6z6B5zwkuTm4/Peak%2BPlus-Badging-and-Content?node-id=495-137216&m=dev
export default function CheckoutWelcome({ closeModal, purchaseTier }: Props) {
  const experiments = useExperiments();
  const user = useUser();

  const handleCloseRequest = useCallback(() => {
    closeModal(user!); // user should always be defined at checkout welcome step.
  }, [closeModal, user]);

  useEffect(() => {
    setTimeout(() => {
      handleCloseRequest();
    }, 3000);
  }, [handleCloseRequest]);

  function getHeading() {
    return purchaseTier === 'peak' ? <FormattedMessage defaultMessage="Welcome to Peak" /> : <FormattedMessage defaultMessage="Welcome to Plus" />;
  }

  function getText() {
    if (purchaseTier === 'peak') {
      if (experiments['web-discover-byot-free-rm']?.value === 'on') {
        return <FormattedMessage defaultMessage="You can now adventure offline, explore community heatmaps, and more" />;
      }

      return experiments['rm-conditions-plus']?.value === 'on' ? (
        <FormattedMessage defaultMessage="You can now build your own routes, explore offline, and more" />
      ) : (
        <FormattedMessage defaultMessage="You can now build your own routes, check on-trail conditions, and more" />
      );
    }

    return <FormattedMessage defaultMessage="You can now download offline maps, get alerts for wrong turns, and more" />;
  }

  function getLogo() {
    return purchaseTier === 'peak' ? <PeakLogoBadge size="xl" variant="icon" /> : <PlusLogoBadge size="xl" variant="icon" />;
  }

  return (
    <ModalStep contentsClassName={styles.contents} hideCloseButton onCloseRequest={handleCloseRequest}>
      <div className={styles.content}>
        {getLogo()}
        <Typography variant="heading400">{getHeading()}</Typography>
        <Typography color="secondary" variant="text200">
          {getText()}
        </Typography>
        <div>
          <Button onClick={handleCloseRequest} testId="lets-go" text={<FormattedMessage defaultMessage="Let’s go" />} />
        </div>
      </div>
    </ModalStep>
  );
}
